import React from 'react';
import { FormControl, Select, MenuItem, Button, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  FilterLoading,
  FilterValue,
  filterInputSx,
  filterMenuProps,
  onlyButtonSx,
  optionItemSx,
  selectAllItemSx,
  summarizeSelection,
} from './ui/filterStyles';
import { useFilterContext } from '../context/FilterContext';

interface BrandsFilterProps {
  orgId: number;
  selectedBrands: string[];
  onBrandsChange: (brands: string[]) => void;
  dashboardType?: string | null;
  isPersonalMetricsPage?: boolean;
}

export const BrandsFilter: React.FC<BrandsFilterProps> = ({
  orgId,
  selectedBrands,
  onBrandsChange,
  dashboardType,
  isPersonalMetricsPage,
}) => {
  const { availableBrands, isInitialized } = useFilterContext();

  const isSingleSelect = dashboardType === 'personal_branding' && isPersonalMetricsPage;
  const label = dashboardType === 'personal_branding' ? 'People' : 'Brands';
  const singularLabel = dashboardType === 'personal_branding' ? 'Person' : 'Brand';

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const value = event.target.value;
    if (isSingleSelect) {
      const selectedId = typeof value === 'string' ? value : '';
      onBrandsChange(selectedId ? [selectedId] : []);
    } else {
      const selectedIds = typeof value === 'string' ? value.split(',') : value as string[];
      onBrandsChange(selectedIds);
    }
  };

  const handleOnly = (brandId: string) => {
    onBrandsChange([brandId]);
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const allBrandIds = availableBrands.map(brand => brand.id);
    onBrandsChange(allBrandIds);
  };

  const getRenderValue = (selectedValue: string | string[]) => {
    if (isSingleSelect) {
      const id = typeof selectedValue === 'string' ? selectedValue : '';
      const selectedBrand = availableBrands.find(b => b.id === id);
      return <FilterValue label={singularLabel} value={selectedBrand ? selectedBrand.name : 'Choose one'} />;
    }
    const selectedArray = Array.isArray(selectedValue) ? selectedValue : [];
    return <FilterValue label={label} value={summarizeSelection(selectedArray, availableBrands)} />;
  };

  if (!isInitialized) {
    return <FilterLoading label={label.toLowerCase()} />;
  }

  return (
    <FormControl fullWidth>
      <Select
        id={`${label.toLowerCase()}-filter`}
        multiple={!isSingleSelect}
        displayEmpty
        value={isSingleSelect ? (selectedBrands[0] || '') : selectedBrands}
        onChange={handleChange}
        renderValue={getRenderValue}
        input={<OutlinedInput sx={filterInputSx} />}
        MenuProps={filterMenuProps}
      >
        {!isSingleSelect && (
        <MenuItem onMouseDown={handleSelectAll} sx={selectAllItemSx}>
          Select all
        </MenuItem>
        )}

        {availableBrands.map(brand => (
          <MenuItem
            key={brand.id}
            value={brand.id}
            sx={optionItemSx}
          >
            {!isSingleSelect && <Checkbox size="small" checked={selectedBrands.indexOf(brand.id) > -1} sx={{ p: 0, mr: 1 }} />}
            <ListItemText primary={brand.name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            {!isSingleSelect && (
            <Button
              className="filter-only"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOnly(brand.id);
              }}
              sx={onlyButtonSx}
            >
              Only
            </Button>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
